<template>
  <dynamic-object-form-structure
    :object-uuid="objectUUID"
    :object-layer="objectLayer"
    :loading-structure="loadingStructure"
    :loading-save="loadingSave"
    :permission-name="form.permission"
    :object-type-translation-key="form.name_translation"
    :tabs="visibleTabs"
    :active-tab="activeTab"
    :action-type="actionType"
    :saveIsDisabled="!required.filled"
    :map-element="$parent.$refs.formsContainer"
    :previous-pos-x="previousPosX"
    :previous-pos-y="previousPosY"
    :data-was-changed="dataWasChanged"
    :fields-still-need-filled="required.stillNeedFilled"
    @updateActiveTab="activeTab = $event"
    @changeViewMode="changeViewModeOfObject"
    @openDetails="openDetails"
    @refresh="refresh"
    @clearData="clearData"
    @save="saveObject"
    @close="closeForm"
    :object-data="objectData"
  >
    <template v-slot:formFields>
      <div
        v-show="visibleTabs[activeTab].type === 'form'"
        class="form tab-content"
      >
        <template v-for="field in visibleTabs[activeTab].fields">
          <input-field
            v-if="field.var_name === 'pw_material_id'"
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <div>
              <treeselect
                :value="getValueInObject(field.var_name, field.input_type)"
                @input="
                  setValueInObject(field.var_name, $event, field.input_type)
                "
                :options="materialOptions"
                :load-options="loadMaterialOptions"
                :normalizer="materialOptionsNormalizer"
                :disabled="
                  readOnly ||
                    !userCanEditTabContent(visibleTabs[activeTab]) ||
                    field.disable
                "
                class="form-input-select"
                disableFuzzyMatching
              />
            </div>
          </input-field>

          <input-field
            v-else
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            @input="setValueInObject(field.var_name, $event, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            :inputType="field.input_type"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :selectOptions="field.select_list_name"
            :radioOptions="field.radio_options"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
            :hide="field.hide"
            :checkboxOptions="field.checkbox_options"
            :object-data="objectData"
            :labelIsLocked="objectData.pw_label_locked"
            :user-can-unlock-label="!userHasFibercloudPermission(form.permission + '_manage_label')"
            @toggle-label-lock="changeLabelMode"
            @updateCheckboxValue="updateCheckboxValue($event)"
            @removePhoto="addToPhotosToRemove($event)"
          />
        </template>
      </div>

      <cables-micro-ducts
        v-show="visibleTabs[activeTab].code === 'pathway_simplified_cables'"
        :pathway-uuid="objectData.pw_uuid"
        :action-type="actionType"
      />

      <asset-reservation-tab
        v-if="actionType !== 'create'"
        v-show="visibleTabs[activeTab].code === 'pathway_asset_reservation'"
        :endpoint="form.endpoint"
        :object-uuid="objectData.pw_uuid"
        :disabled="readOnly"
        :fields="visibleTabs[activeTab].fields"
      />

      <accessories-and-others-tab
        v-show="visibleTabs[activeTab].label === 'Accessories'"
        :accessories-selected="accessoriesSelected"
        :accessories-selected-by-material-id="accessoriesSelectedByMaterialId"
        relation-family-id-select="pt"
        family-id="ac"
        :disabled="readOnly"
      />
      <accessories-and-others-tab
        v-show="visibleTabs[activeTab].label === 'Others'"
        :accessories-selected="othersSelected"
        :accessories-selected-by-material-id="othersSelectedByMaterialId"
        relation-family-id-select="pt"
        family-id="ot"
        :disabled="readOnly"
      />
      <custom-fields-tab
        v-if="visibleTabs[activeTab].code === 'custom_fields'"
        :customFields="customFields.custom_fields"
        :objectData="objectData.custom_fields"
        :actionType="actionType"
        :disabled="readOnly"
        @updateValue="setValueInObject"
        :requiredFilled="required.filled"
      />
    </template>
  </dynamic-object-form-structure>
</template>

<script>
import { bus } from "@/main";
import { dynamicObjectFormsMixin } from "@/mixins/forms/dynamicObjectForms/dynamicObjectFormsMixin";
import DynamicObjectFormStructure from "../_Templates/DynamicObjectFormStructure";
import InputField from "../_Molecules/InputField";
import PathwaySimplifiedCables from "@/components/Home/Forms/_SpecialTabs/PathwaySimplifiedCables";
import AssetReservationTab from "@/components/Home/Forms/_SpecialTabs/AssetReservationTab/AssetReservationTab";
import AccessoriesAndOthersTab from "@/components/Home/Forms/_SpecialTabs/AccessoriesAndOthers/AccessoriesAndOthersTab";
import CustomFieldsTab from "@/components/Home/Forms/_SpecialTabs/CustomFields/CustomFieldsTab.vue";
import CablesMicroDucts from "@/components/Home/Forms/_SpecialTabs/Pathways/CablesMicroDucts";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "PathwayForm",

  components: {
    AccessoriesAndOthersTab,
    AssetReservationTab,
    PathwaySimplifiedCables,
    InputField,
    DynamicObjectFormStructure,
    CustomFieldsTab,
    CablesMicroDucts
  },

  props: {
    lineStringWKT: {
      type: [Array, String]
    },
    linkPathwayToPathway: {
      type: Object
    },
    pathwayNodes: {
      type: Array
    }
  },

  mixins: [dynamicObjectFormsMixin,checkPermissionsMixin],

  computed: {
    isPathwayUnderground() {
      return this.objectData.pw_instalation_type == 7;
    },

    materialRequestQueryParameters() {
      return {
        material_family_id: "pt"
      };
    }
  },

  watch: {
    isPathwayUnderground(newValue) {
      this.updateInstallationTypeDependentFields();
      if (!newValue) {
        this.objectData.pw_logical_type = "4";
        //this.objectData.pw_surface = "6";
        this.objectData.pw_material = "7";
      }
    },

    "objectData.pw_material_id"(newID) {
      this.updateMaterialDependentFields(newID);
    },

    materialOptions() {
      if (this.materialOptions && this.objectData.pw_material_id) {
        this.updateMaterialDependentFields(this.objectData.pw_material_id);
      }
    }
  },

  methods: {
    tabIsEnabled(tab) {
      return !(
        tab.code === "pathway_asset_reservation" && this.actionType === "create"
      );
    },

    createObject() {
      this.createDataToSend();

      if (this.$attrs.creatingBulkTP) {
        this.updateObjectLocalStorageData();
        this.closeForm();
        this.$emit("update-pathway-data", this.dataToSend);
      } else {
        this.CREATE_OBJECTS({
          endpoint: this.form.endpoint,
          data: [this.dataToSend]
        })
          .then(() => {
            this.resetParentValues();
            this.finishObjectCreation();
          })
          .catch(error => {
            this.$toasted.global.toast_error(error.response.data.data);
            this.loadingSave = false;
          });
      }
    },

    createDataToSend() {
      this.dataToSend = {
        text_geom: this.lineStringWKT,
        projection: 3857,
        progress: this.objectData.pw_progress,
        status: this.objectData.pw_status
      };

      if (this.pathwayNodes !== undefined) {
        this.dataToSend.pathway_nodes = this.pathwayNodes;
      }

      if (
        this.linkPathwayToPathway &&
        (this.linkPathwayToPathway.startPathwayData != null ||
        this.linkPathwayToPathway.endPathwayData != null)
      ) {
        this.dataToSend.pathway = {
          splitStart: this.linkPathwayToPathway.startPathwayData != null,
          splitEnd: this.linkPathwayToPathway.endPathwayData != null
        };
        if (this.dataToSend.pathway.splitStart) {
          this.dataToSend.pathway.start_uuid = this.linkPathwayToPathway.startPathwayData.uuid;
        }
        if (this.dataToSend.pathway.splitEnd) {
          this.dataToSend.pathway.end_uuid = this.linkPathwayToPathway.endPathwayData.uuid;
        }
      }
      if (this.accessoriesSelected || this.othersSelected) {
        this.dataToSend.accessories = [];
        this.generateAccessoriesDataSend(this.accessoriesSelected);
        this.generateAccessoriesDataSend(this.othersSelected);
      }
      this.placeObjectDataInDataToSend();
    },

    resetParentValues() {
      bus.$emit("reset-pathway-and-split-data");
      bus.$emit("reset-line-string");
    },

    updateInstallationTypeDependentFields() {
      let fields = this.form.tabs[0].fields;

      //this.updateInstallationTypeDependentField(fields, "pw_surface");
      this.updateInstallationTypeDependentField(fields, "pw_material");
      this.updateInstallationTypeDependentField(fields, "pw_logical_type");
    },

    updateInstallationTypeDependentField(fields, varName) {
      let dependentField = fields.find(field => field.var_name === varName);
      if (dependentField) {
        dependentField.hide = !this.isPathwayUnderground;
        dependentField.required = this.isPathwayUnderground;
      }
    },

    /** Emits an event to close this component's window **/
    closeForm() {
      bus.$emit("enable-click-events");
      this.resetParentValues();
      this.$emit("close");
    },

    updateObjectLocalStorageData() {
      let objectData = this.objectData;
      Object.keys(objectData).forEach(key => {
        if (
          key === "node_target" ||
          key === "node_source" ||
          key === "node" ||
          key === "pathway"
        ) {
          delete objectData[key];
        }
      });
      sessionStorage.setItem(
        this.form.local_storage_name,
        JSON.stringify(objectData)
      );
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/ObjectForms/PathwayForm.vue